import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { googleExchange, TwoFactorChallenge, complete2FA } from "../services/authService";

const GoogleCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"exchanging" | "2fa" | "error" | "ok">("exchanging");
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcode = params.get("code");
    const gstate = params.get("state") || undefined;
    const gerror = params.get("error");
    if (gerror) {
      setError(`Google returned an error: ${gerror}`); setStatus("error"); return;
    }
    if (!gcode) {
      setError("Missing code from Google callback"); setStatus("error"); return;
    }
    (async () => {
      try {
        const res = await googleExchange(gcode, gstate);
        if ((res as TwoFactorChallenge).requires_2fa) {
          setChallenge(res as TwoFactorChallenge);
          setStatus("2fa");
        } else {
          setStatus("ok");
          // Full reload so AuthContext picks up the token from localStorage
          setTimeout(() => { window.location.href = "/"; }, 600);
        }
      } catch (e: any) {
        setError(e?.detail || e?.message || "Google sign-in failed");
        setStatus("error");
      }
    })();
  }, []);

  const submitCode = async () => {
    if (!challenge) return;
    setBusy(true); setError(null);
    try {
      await complete2FA(challenge.temp_token, code.trim());
      setStatus("ok");
      setTimeout(() => { window.location.href = "/"; }, 500);
    } catch (e: any) {
      setError(e?.detail || e?.message || "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-4 text-center">
        {status === "exchanging" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
            <p className="text-sm text-slate-600">Signing you in with Google…</p>
          </>
        )}
        {status === "2fa" && (
          <>
            <p className="text-lg font-semibold text-slate-800">Enter your authenticator code</p>
            <p className="text-sm text-slate-500">
              Your Google account is verified. Please provide your 2FA code to finish signing in.
            </p>
            <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
              placeholder="000000" maxLength={16}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono text-lg tracking-widest text-center outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus />
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-2.5 flex items-start gap-2 text-left">
                <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
              </div>
            )}
            <button onClick={submitCode} disabled={busy || code.trim().length < 6}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 shadow">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Verify
            </button>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto" />
            <p className="text-sm text-slate-600">Signed in. Redirecting…</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="w-8 h-8 text-red-600 mx-auto" />
            <p className="text-sm text-red-700">{error || "Sign-in failed"}</p>
            <button onClick={() => navigate("/login")} className="text-sm text-emerald-700 hover:underline">Back to login</button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
